import { QueryKnowledgeGraphTool } from '#plugin-chat/components/tools/query-knowledge-graph/QueryKnowledgeGraphTool.tsx';
import { RenderShowDashboardTool } from '#plugin-chat/components/tools/show-dashboard/ShowDashboardTool.render.tsx';
import {
  type ShowDashboardInput,
  ShowDashboardInputSchema,
  type ShowDashboardOutput,
} from '#plugin-chat/components/tools/show-dashboard/schema.ts';
import {
  collectReferencedSourceKeys,
  validateDashboardSpec,
} from '#plugin-chat/components/tools/show-dashboard/validate.ts';
import { TransformDatasetTool } from '#plugin-chat/components/tools/transform-dataset/TransformDatasetTool.tsx';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';
import { Json, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import type { ToolConfig } from '#plugin-chat/lib/tool/create-tool.types.ts';
import { DatasetNotFoundError, InvalidSpecError } from '#plugin-chat/lib/tool/errors/index.ts';

/** Tool for generating dashboard visualizations in chat. */
export const ShowDashboardTool = createTool('showDashboard', () => ({
  instructions: (
    <ToolGuidance
      tool={ShowDashboardTool}
      whenToUse={[
        'Display results from other tool calls',
        'Display knowledge graph data (KPI or table)',
        'Display timeseries data (line/area/bar/scatter/other charts)',
        'Display KPIs',
      ]}
    >
      <p>
        <strong>Always use {ShowDashboardTool.mention} to display results from other tools</strong>, including results
        from {QueryKnowledgeGraphTool.mention} and {TransformDatasetTool.mention}. Never type the data itself as a
        markdown list, bullet list, or inline table — that is a hard failure mode. If the message would contain more
        than ~3 items from a dataset, render it as a dashboard <code>table</code>, <code>kpi</code>, or
        <code>chart</code> instead. Prose is reserved for the narrative around the dashboard (question, insight, next
        steps) — never for the rows themselves. When a single value is the answer (e.g. a count), render it as a
        <code>kpi</code> rather than a sentence.
      </p>

      <h3>Item structure</h3>
      <p>
        Each item has position, component, and optional inputDataset/caption. The component field uses a tagged-object
        format: the key IS the component type.
      </p>
      <ul>
        <li>{'table: { "component": { "table": { "columns": [...] } } }'}</li>
        <li>
          {
            'chart: { "component": { "chart": { "chartType": "line", "series": [...] } } } — omit `xAxis` for timeseries data from `getDatapoints`; it defaults to the `timestamp` column'
          }
        </li>
        <li>{'kpi: { "component": { "kpi": { "value": 42, "label": "Revenue" } } }'}</li>
      </ul>

      <h3>Human-readable labels (tables &amp; charts)</h3>
      <p>
        Column accessors and series names from {TransformDatasetTool.mention} (e.g. <code>SE0074_L_avg</code>,
        <code>productionRate</code>) are auto-humanized to Title Case as a fallback (<code>SE0074 L Avg</code>,
        <code>Production Rate</code>). The auto-humanizer cannot recover domain context — when the user-facing label
        carries meaning the accessor cannot (well names, units, descriptive metric names), supply an explicit
        <code>title</code> per table column and an explicit <code>label</code> per chart series. Examples:
        <code>{'{ accessor: "SE0074_L_avg", title: "SE0074 Long – Avg Priority Oil Rate (Sm³/d)" }'}</code>,
        <code>{'{ name: "SE0074_L_avg", label: "SE0074 Long" }'}</code>.
      </p>

      <h3>Chart types</h3>
      <dl>
        <dt>line/area</dt>
        <dd>Time series, trends</dd>
        <dt>bar</dt>
        <dd>Part-to-whole (6 or fewer parts)</dd>
        <dt>scatter/bubble</dt>
        <dd>Correlations</dd>
        <dt>composite</dt>
        <dd>Multiple series types</dd>
      </dl>

      <h3>Reference lines</h3>
      <p>
        {/* Add referenceLines to cartesian charts (line, area, bar, composite) to highlight thresholds, limits, targets, or */}
        {/* averages. Use y for horizontal lines (numeric thresholds) and x for vertical lines (event dates or categories). */}
        Include reference lines when the data context provides known limits, targets, regulatory thresholds, safety
        boundaries, or computed averages/medians — these add critical context that makes charts immediately actionable.
      </p>

      <h3>Binary and status data</h3>
      <p>
        For binary values (0/1 like online/offline status) or discrete state data, use curveType "step". Translate raw
        numeric states into human-readable labels in the chart caption and axis (e.g., "Online" / "Offline", not "1 =
        online, 0 = offline").
      </p>

      <h3>Chart series data binding (inputDataset + column)</h3>
      <p>
        For <code>chart</code> items, each entry in <code>series</code> declares its own <code>inputDataset</code> (the
        outputDataset of a dataset) + <code>column</code> (the column within that dataset). Different series MAY
        reference different datasets — use this to overlay curves like daily vs. weekly-averaged values of the same
        metric on a shared <code>xAxis</code>. Do <strong>not</strong> put <code>inputDataset</code> at the item level
        for charts; it is ignored for chart items.
      </p>
      <p>
        <strong>Each series must map to a distinct column.</strong> Two series on the same dataset cannot both target
        the same <code>column</code> (e.g. two series with <code>column: "rate"</code>) — the chart adapter keys series
        by column name and duplicates collapse into a single line, producing a broken render.
      </p>
      <p>
        <strong>Datapoint-array columns must be expanded upstream.</strong> When your pipeline produces rows that hold
        arrays of <code>{'{timestamp, value | average | …}'}</code> objects (i.e. output of <code>daily()</code> /
        <code>hourly()</code> / <code>monthly()</code> / <code>datapoints()</code> derives), end the pipeline with an
        <code>expandTimeseries</code> action in {TransformDatasetTool.mention}. That produces a wide-format dataset with
        a canonical <code>timestamp</code> column plus one numeric column per series, which the chart renders directly.
        Do <strong>not</strong> use <code>unroll</code> / <code>spread</code> on datapoint-array columns — the engine
        refuses those.
      </p>
      <p>
        Use <code>splitBy</code> to pick which column identifies each row's series. With one input column,
        <code>splitBy</code> values become the output column names (one column per entity). With multiple input columns,
        output columns are named <code>{'<splitBy>__<column>'}</code> (e.g. <code>SE0070-S__weeklyMin</code>,
        <code>SE0070-S__weeklyMax</code>).
      </p>
      <p>End-to-end example — daily oil rate for the top 5 producers in field Foo:</p>
      <Json
        value={{
          inputDataset: 'foo_field_wells',
          outputDataset: 'top5_daily_chart',
          actions: [
            { type: 'filter', where: { type: 'and', filters: [{ column: 'field', op: 'eq', stringValue: 'Foo' }] } },
            {
              type: 'derive',
              derives: [{ name: '_rank', expression: 'mean(values(monthly(prodRef)))' }],
            },
            { type: 'sort', keys: [{ column: '_rank', direction: 'desc' }] },
            { type: 'slice', limit: 5 },
            {
              type: 'derive',
              derives: [{ name: 'daily', expression: "daily(prodRef, {start: '2026-01-01', end: '2026-02-01'})" }],
            },
            { type: 'expandTimeseries', columns: ['daily'], splitBy: 'wellString.externalId' },
          ],
        }}
      />

      <h3>Long-format data: pivot before charting</h3>
      <p>
        If your dataset has <em>one row per (x, group)</em> with a discriminator column identifying the group (e.g. rows
        shaped <code>{'{ timestamp, series, rate }'}</code> where <code>series</code> holds the well/tag/entity id), it
        is <strong>long format</strong> and cannot be charted directly — every chart series would have to reference the
        same <code>rate</code> column, violating the distinct-column rule above. Reshape to
        <strong>wide format</strong> (one row per x, one column per group) with {TransformDatasetTool.mention} using
        <code>pivot</code> first, then point each chart series at the resulting per-group columns.
      </p>
      <p>
        Pivot example — long → wide on the <code>series</code> discriminator:
      </p>
      <Json
        value={{
          inputDataset: 'wells_long',
          outputDataset: 'wells_wide',
          actions: [{ type: 'pivot', keyColumn: 'series', valueColumn: 'rate' }],
        }}
      />
      <p>
        The resulting dataset has one row per <code>timestamp</code> with one column per distinct <code>series</code>
        value (e.g. <code>"ST010-L:…"</code>, <code>"ST006-L:…"</code>, <code>"ST008-S:…"</code>). Each chart series
        then points at one of those columns:
      </p>
      <Json
        value={{
          series: [
            { inputDataset: 'wells_wide', column: 'ST010-L:WellOutputs:priorityOilRateStd', label: 'ST010-L' },
            { inputDataset: 'wells_wide', column: 'ST006-L:WellOutputs:priorityOilRateStd', label: 'ST006-L' },
            { inputDataset: 'wells_wide', column: 'ST008-S:WellOutputs:priorityOilRateStd', label: 'ST008-S' },
          ],
        }}
      />

      <h3>Radar charts: transpose first</h3>
      <p>
        Radar needs rows = spoke categories and columns = polygons. If your source has the opposite shape (e.g. rows =
        wells, columns = metrics), call <code>transformDataset</code> first with a reshape/pivot operation to transpose
        into the radar-ready shape, then point every series at that transposed dataset. A common pattern:
      </p>
      <ol>
        <li>
          <code>transformDataset</code> — reshape wells-as-rows into metric-as-rows: one row per metric (e.g.
          <code>metric = "oil"</code>, <code>"gas"</code>, <code>"water"</code>), with one column per entity (well).
        </li>
        <li>
          Chart series — one entry per entity (polygon), each with <code>inputDataset</code> pointing at the reshaped
          dataset and <code>column</code> set to that entity's column. Set <code>xAxis</code> to the metric-label column
          (e.g. <code>"metric"</code>).
        </li>
      </ol>

      {/* <h3>Layout (1-indexed positions)</h3> */}
      {/* <dl> */}
      {/*   <dt>Single item</dt> */}
      {/*   <dd>1x1 layout, position row 1 column 1</dd> */}
      {/*   <dt>Multiple items</dt> */}
      {/*   <dd>KPIs in row 1, charts/tables in rows 2+</dd> */}
      {/* </dl> */}
      {/* <p>Set layout.columns to match needs, use columnSpan for width.</p> */}

      <h3>Example Dashboard</h3>
      <Json
        value={{
          layout: { rows: 1, columns: 2 },
          items: [
            {
              position: { row: 1, column: 1, rowSpan: 1, columnSpan: 1 },
              component: { kpi: { value: 42, label: 'Total Items' } },
            },
            {
              position: { row: 1, column: 2, rowSpan: 1, columnSpan: 1 },
              inputDataset: 'query_result',
              component: { table: { columns: [{ accessor: 'name', title: 'Name' }] } },
            },
          ],
        }}
      />
    </ToolGuidance>
  ),
  config: {
    parameters: ShowDashboardInputSchema,
    render: RenderShowDashboardTool,
    description: <p>Generate a dashboard with charts, tables and KPIs in a grid layout.</p>,
    execute: async (args, { ctx }) => {
      // Resolve every dataset referenced by the spec before validating —
      // the render-layer adapters need the same dataset shapes to decide
      // whether chart series map onto real columns. Running validation
      // here means the LLM sees a structured error on its turn instead of
      // a silent <BlockError> the user has to explain back.
      //
      // Missing datasets stay as `undefined` in the map so
      // validateDashboardSpec can emit per-reference rendering errors;
      // we swallow DatasetNotFoundError here rather than letting
      // the resolver's structured envelope short-circuit the whole
      // validation pass.
      const entries = await Promise.all(
        collectReferencedSourceKeys(args.items).map(async (key) => {
          try {
            return [key, await ctx.resolve.byName(key)] as const;
          } catch (err) {
            if (err instanceof DatasetNotFoundError) {
              return [key, undefined] as const;
            }
            throw err;
          }
        }),
      );
      const datasets = new Map<string, Dataset | undefined>(entries);

      const errors = validateDashboardSpec(args.items, datasets);
      if (errors.length > 0) {
        const header =
          errors.length > 1
            ? `Dashboard spec has ${errors.length} rendering errors:`
            : 'Dashboard spec has a rendering error:';
        throw new InvalidSpecError(`${header}\n- ${errors.join('\n- ')}`, {
          suggestions: [
            `Inspect each series' column against the dataset's columns — ${TransformDatasetTool.mention} outputs list them under \`columns\`.`,
            `When your pipeline produces datapoint-array columns (output of daily()/weekly()/monthly()/datapoints() derives), end it with an \`expandTimeseries\` action in ${TransformDatasetTool.mention} — \`{ type: 'expandTimeseries', columns: ['<arrCol>'], splitBy: '<id-column>' }\` — to materialize the canonical \`timestamp\` column and one numeric column per series.`,
            `For long-format data (one row per (x, group) with a discriminator column), call ${TransformDatasetTool.mention} with a \`pivot\` action before charting.`,
            'Ensure every inputDataset referenced in items exists as a staged dataset outputDataset.',
          ],
        });
      }

      return {
        success: true as const,
        message: 'The dashboard has been rendered above this message.',
      };
    },
  } satisfies ToolConfig<ShowDashboardInput, ShowDashboardOutput>,
}));
